/**
 * FieldInsight / Open Claw canonical design tokens.
 *
 * These values are the source of truth for brand colour, AI-action
 * accents, and the neutral scale. They MUST match the canonical AI
 * Styling Pattern published at:
 *     https://fi-ai-styling-guide-3413f27b698e.herokuapp.com/pattern.html
 *
 * If you need a new colour, add it here first — do NOT hard-code hex
 * values in components. Hard-coded hex across the codebase was the
 * single biggest audit gap (AUDIT-v1.5.2.md §2).
 *
 * Usage:
 *     import { BRAND, BRAND_LIGHT, INK, SUCCESS } from '@/theme/tokens';
 *     <Box sx={{ color: BRAND, backgroundColor: BRAND_LIGHT }}>…</Box>
 */

// ─────────────────────────────────────────────────────────────
// Brand — AI-action colour. Used for every sparkle, every AI pill,
// every provenance tag. Locked by the AI Styling Pattern.
// ─────────────────────────────────────────────────────────────
export const BRAND         = '#39006B'; // primary AI brand purple
export const BRAND_HOVER   = '#4A0A82'; // interactive hover state
export const BRAND_LIGHT   = '#F5EEFF'; // surface tint (badges, backgrounds)
export const BRAND_BORDER  = '#E4D4F5'; // soft outline on brand-light surfaces

// ─────────────────────────────────────────────────────────────
// Legacy orange — retained ONLY for non-AI app chrome (status chips,
// job colour-coding, the top-bar tab underline). NEVER use for any
// AI-triggered action. If you find yourself reaching for this in an
// AI context you're using the wrong token.
// ─────────────────────────────────────────────────────────────
export const FI_ORANGE     = '#F57C00';
export const FI_ORANGE_ALT = '#F38F18'; // deeper orange used by JobHoverCard

// ─────────────────────────────────────────────────────────────
// Ink — text + dark surfaces.
// ─────────────────────────────────────────────────────────────
export const INK           = '#1A1A2E';
export const INK_SOFT      = '#4A4A5E';
export const INK_MUTE      = '#7A7A8E';

// ─────────────────────────────────────────────────────────────
// Surface — backgrounds.
// ─────────────────────────────────────────────────────────────
export const BG            = '#FFFFFF';
export const BG_ALT        = '#FAFAFC';
export const BORDER        = '#E8E8EE';

// ─────────────────────────────────────────────────────────────
// Semantic status — for reconciliation states, variance badges,
// approved/closed pills. Colour-coded so a PM can scan at a glance.
// ─────────────────────────────────────────────────────────────
export const SUCCESS_BG    = '#DCFCE7';
export const SUCCESS_FG    = '#166534';
export const WARNING_BG    = '#FEF2F2';
export const WARNING_FG    = '#991B1B';
export const VARIANCE_BG   = '#FFF4E4'; // amber — price / qty variance (acknowledge required)
export const VARIANCE_FG   = '#B56414';

// ─────────────────────────────────────────────────────────────
// Status chip palette — the single source of truth for every
// reconciliation / workflow state in the app. Used by
// `<StatusChip>` (src/components/StatusChip.tsx) and the
// `getStatusTone()` helper below.
//
// Tone families:
//   neutral  — Draft, paused, archived
//   info     — Submitted, Active (in queue, not yet actioned)
//   primary  — Approved (brand-purple — locked-in commitment)
//   warn     — Partially Received, Awaiting Approval, In Progress
//   success  — Received, Bill Received, Completed, Paid
//   danger   — Disputed, Canceled
//
// Every chip uses the same shape (rounded 999, h=20, semibold,
// 1px border in the FG colour at 25% opacity). Only the colour
// pair changes per status. PMs can scan an entire screen at a
// glance — and a single hex change in tokens.ts re-skins every
// chip in the app.
// ─────────────────────────────────────────────────────────────
export type StatusTone = 'neutral' | 'info' | 'primary' | 'warn' | 'success' | 'danger';

export interface StatusToneTokens {
  bg: string;
  fg: string;
  /** Border colour — keep at fg + low alpha for soft separation on white tables. */
  border: string;
}

export const STATUS_TONE: Record<StatusTone, StatusToneTokens> = {
  neutral: { bg: '#F1F1F4', fg: '#5A5A6E', border: '#5A5A6E40' },
  info:    { bg: '#E3F2FD', fg: '#0D47A1', border: '#0D47A140' },
  primary: { bg: BRAND_LIGHT, fg: BRAND,   border: BRAND_BORDER },
  warn:    { bg: '#FFF3E0', fg: '#B56414', border: '#B5641440' },
  success: { bg: SUCCESS_BG, fg: SUCCESS_FG, border: '#16653440' },
  danger:  { bg: '#FFEBEE', fg: '#B71C1C', border: '#B71C1C40' },
};

/**
 * Map a workflow status string to its canonical tone. Centralised so
 * BillsList / POList / Statements / inline panels all agree.
 *
 * Unknown statuses fall back to neutral — never a raw hex.
 */
export function getStatusTone(status: string): StatusTone {
  switch (status) {
    // PO + Bill shared
    case 'Draft':
    case 'Archived':
    case 'Paused':
      return 'neutral';
    case 'Submitted':
    case 'Active':
    case 'Awaiting Acceptance':
      return 'info';
    case 'Approved':
    case 'Quote Accepted':
    case 'Job Created':
      return 'primary';
    case 'Awaiting approval':
    case 'Partial received':
    case 'Partially Received':
    case 'Pending Goods Receipt':
    case 'Bill Pending Goods':
    case 'In Progress':
    case 'Follow Up':
      return 'warn';
    case 'All received':
    case 'Received':
    case 'Bill received':
    case 'Bill Received':
    case 'Finalised':
    case 'Completed':
    case 'Paid':
    case 'Job Completed':
    case 'Sent':
      return 'success';
    case 'Disputed':
    case 'Canceled':
    case 'Cancelled':
    case 'Quote Declined':
    case 'Void':
      return 'danger';
    case 'Superseded':
      return 'neutral';
    default:
      return 'neutral';
  }
}

// ─────────────────────────────────────────────────────────────
// Convenience: re-export everything as a single frozen object for
// places that prefer a single import over named imports.
// ─────────────────────────────────────────────────────────────
export const TOKENS = Object.freeze({
  BRAND,
  BRAND_HOVER,
  BRAND_LIGHT,
  BRAND_BORDER,
  FI_ORANGE,
  FI_ORANGE_ALT,
  INK,
  INK_SOFT,
  INK_MUTE,
  BG,
  BG_ALT,
  BORDER,
  SUCCESS_BG,
  SUCCESS_FG,
  WARNING_BG,
  WARNING_FG,
  VARIANCE_BG,
  VARIANCE_FG,
  STATUS_TONE,
});

export default TOKENS;
